export const APP_STORE_URL = 'https://apps.apple.com/us/app/pebla-track-mobile-money-pay/id6748704464';

const ANDROID_PACKAGE = 'com.pebla.pebla';
const APP_LINK_HOST = 'app.pebla.io';

/**
 * Builds the Play Store URL with a `referrer` param carrying the deferred
 * deep-link payload (token + optional phone), double-encoded per Play
 * Store's install-referrer mechanism.
 */
export function buildPlayStoreUrl(token: string, phone?: string | null): string {
    let innerPayload = `token=${encodeURIComponent(token)}`;
    if (phone) innerPayload += `&phone=${encodeURIComponent(phone)}`;
    return (
        `https://play.google.com/store/apps/details?id=com.pebla.pebla` +
        `&referrer=${encodeURIComponent(innerPayload)}`
    );
}

/**
 * Builds the custom-scheme deep link used for iOS clipboard handoff.
 */
export function buildIosDeepLink(token: string, phone?: string | null): string {
    let link = `pebla://ref?token=${encodeURIComponent(token)}`;
    if (phone) link += `&phone=${encodeURIComponent(phone)}`;
    return link;
}

/**
 * Builds an `intent://` URL that asks Chrome for Android to open the app
 * directly (same App Link the OS already verifies for app.pebla.io), falling
 * back to the Play Store URL if the app isn't installed or the browser
 * doesn't support the intent syntax.
 */
export function buildAndroidIntentUrl(token: string, phone?: string | null): string {
    const fallbackUrl = buildPlayStoreUrl(token, phone);
    const query = phone ? `?phone=${encodeURIComponent(phone)}` : '';
    return (
        `intent://${APP_LINK_HOST}/r/${encodeURIComponent(token)}${query}` +
        `#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`
    );
}

const APP_LINK_PATH = /^\/go(\/[A-Za-z0-9_-]{1,128}){1,3}$/;

/**
 * Returns `pathname` if it is an in-app route link (`/go/…`, e.g.
 * `/go/loyalty/{restaurantId}`) that is safe to hand to the app, else null.
 */
export function appLinkPathFrom(pathname: string): string | null {
    const path = pathname.replace(/\/+$/, '');
    return APP_LINK_PATH.test(path) ? path : null;
}

/**
 * Play Store URL whose install referrer carries an in-app route
 * (`link=/go/…`), so the app opens it after a fresh install.
 */
export function buildPlayStoreUrlForPath(path: string): string {
    const innerPayload = `link=${encodeURIComponent(path)}`;
    return (
        `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}` +
        `&referrer=${encodeURIComponent(innerPayload)}`
    );
}

/**
 * Custom-scheme link for an in-app route; also what iOS reads back from the
 * clipboard on first launch.
 */
export function buildIosDeepLinkForPath(path: string): string {
    return `pebla://app${path}`;
}

/**
 * `intent://` URL that opens an in-app route in the app, falling back to the
 * Play Store with the route in the install referrer.
 */
export function buildAndroidIntentUrlForPath(path: string): string {
    const fallbackUrl = buildPlayStoreUrlForPath(path);
    return (
        `intent://${APP_LINK_HOST}${path}` +
        `#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`
    );
}

/**
 * Races an app-open attempt (custom scheme / intent navigation) against a
 * timeout. Resolves `true` if the tab is backgrounded/unloaded before the
 * timeout fires (the OS handed the tap to the app), `false` if the page is
 * still visible when the timer runs out (app not installed / didn't open).
 */
export function waitForAppOpen(timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
        let settled = false;
        const finish = (opened: boolean) => {
            if (settled) return;
            settled = true;
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            clearTimeout(timer);
            resolve(opened);
        };
        const onVisibilityChange = () => {
            if (document.hidden) finish(true);
        };
        const onPageHide = () => finish(true);

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);
        const timer = window.setTimeout(() => finish(false), timeoutMs);
    });
}
